import { DataStore } from '@common/data-models/data-store/data-store';
import { ResultGroup, ResultGroupingDefinition } from '@common/data-models/search-results/result-group';
import { SearchResults, TreeLikeNode } from '@common/data-models/search-results/search-results';
import { ObjectTypes } from '@common/enums/object-types';
import { NewlyCreatedRecords } from '@suite/common/data-models/newly-created-records/newly-created-records';
import { IPlaybookService } from '@suite/playbook/interfaces/i-playbook-service';
import { map, takeUntil } from 'rxjs/operators';

export class PlaybookSearchResults extends SearchResults {
    public topics: Array<StorytellingTopicRecord> = [];
    public assignments: Array<StorytellingAssignmentRecord> = [];
    public templates: Array<StorytellingTopicRecord> = [];
    public storyTemplates: Array<StorytellingStoryRecord> = [];
    public stories: Array<StorytellingStoryRecord> = [];
    public static playbookService: IPlaybookService = null;

    constructor() {
        super();
        PlaybookSearchResults.newlyAddedItems = new NewlyCreatedRecords();
    }

    // TODO any
    public getAllResults(): Array<any> {
        const ret: Array<StorytellingRecord> = [];
        for (const topic of this.topics) {
            ret.push(topic);
        }
        for (const assignment of this.assignments) {
            ret.push(assignment);
        }
        for (const story of this.stories) {
            ret.push(story);
        }
        for (const template of this.templates) {
            ret.push(template);
        }
        return ret;
    }

    public topicGroups: { [id: string]: ResultGroup<StorytellingTopicRecord> } = {};
    public assignmentSearchGroup: ResultGroup<StorytellingAssignmentRecord> = null;
    public topicSearchGroup: ResultGroup<StorytellingTopicRecord> = null;
    public assignmentGroups: { [id: string]: ResultGroup<StorytellingAssignmentRecord> } = {};
    public storySearchGroup: ResultGroup<StorytellingStoryRecord> = null;
    public storyGroups: { [id: string]: ResultGroup<StorytellingStoryRecord> } = {};

    public static topicGroupDefs: { [id: string]: ResultGroupingDefinition } = {};
    private get topicGroupFields(): Array<string> {
        return [];
    }

    public static storyGroupDefs: { [id: string]: ResultGroupingDefinition } = {};
    private get storyGroupFields(): Array<string> {
        // return PlaybookSearchResults.playbookService.groupByStoryKeys;
        return [];
    }

    public static storySearchGroupDef: { [id: string]: ResultGroupingDefinition } = {};

    public static assignmentSearchGroupDef: { [id: string]: ResultGroupingDefinition } = {};

    public static assignmentGroupDefs: { [id: string]: ResultGroupingDefinition } = {};
    private get assignmentGroupFields(): Array<string> {
        return [];
    }

    public addTopic(topic: StorytellingTopicRecord) {
        this.topics.push(topic);
        this.byGuid[topic.Guid] = topic;
    }

    public addStory(story: StorytellingStoryRecord) {
        this.stories.push(story);
        this.byGuid[story.Guid] = story;
    }

    public addTemplate(template: StorytellingTopicRecord) {
        this.templates.push(template);
        // this.byGuid[template.guid] = template;
    }

    public addStoryTemplate(template: StorytellingTopicRecord) {
        this.storyTemplates.push(template);
    }

    public insertAfterTopic(topic: StorytellingTopicRecord, topicToInsert: StorytellingTopicRecord, isNew: boolean) {
        if (!isNew) {
            this.topics.push(topicToInsert);
            this.byGuid[topicToInsert.Guid] = topicToInsert;
            if (this.objectType === ObjectTypes.TOPIC) {
                this.topicGroups[PlaybookSearchResults.groupByField].insertAfterEntry(
                    PlaybookSearchResults.topicGroupDefs[PlaybookSearchResults.groupByField].valueConvertor(topic),
                    topic,
                    topicToInsert
                );
            } else if (!this.objectType) {
                this.topicSearchGroup.insertAfterEntry(PlaybookSearchResults.topicGroupDefs.SingleGroup.valueConvertor(topic), topic, topicToInsert);
            }
        } else {
            for (let i = 0; i < PlaybookSearchResults.newlyAddedItems.items.length; i++) {
                if (PlaybookSearchResults.newlyAddedItems.items[i].Guid === topic.Guid) {
                    PlaybookSearchResults.newlyAddedItems.addRecord(topicToInsert, i);
                    break;
                }
            }
            if (!PlaybookSearchResults.newlyAddedItems.items.length) {
                PlaybookSearchResults.newlyAddedItems.addRecord(topicToInsert);
            }
        }
        this.buildTree(PlaybookSearchResults.groupByField);
    }

    public insertAfterStory(insertAfter: StorytellingStoryRecord, storyToInsert: StorytellingStoryRecord) {
        this.stories.push(storyToInsert);
        this.byGuid[storyToInsert.Guid] = storyToInsert;
        if (this.objectType === ObjectTypes.SCRIPT) {
            const groups = this.getGroups();
            for (const field of groups) {
                if (this.storyGroups[field]) {
                    this.storyGroups[field].insertAfterEntry(PlaybookSearchResults.storyGroupDefs[field].valueConvertor(insertAfter), insertAfter, storyToInsert);
                    this.buildTree(field);
                }
            }
        } else if (this.storySearchGroup) {
            this.storySearchGroup.addToGroups(PlaybookSearchResults.storySearchGroupDef[PlaybookSearchResults.sortByField].valueConvertor(storyToInsert), storyToInsert);
        }
    }

    public insertAfterAssignment(insertAfter: StorytellingAssignmentRecord, assignmentToInsert: StorytellingAssignmentRecord) {
        this.assignments.push(assignmentToInsert);
        this.byGuid[assignmentToInsert.Guid] = assignmentToInsert;
        if (this.objectType === ObjectTypes.ASSIGNMENT) {
            const groups = this.getGroups();
            for (const field of groups) {
                if (this.assignmentGroups[field]) {
                    this.assignmentGroups[field].insertAfterEntry(PlaybookSearchResults.assignmentGroupDefs[field].valueConvertor(insertAfter), insertAfter, assignmentToInsert);
                    this.buildTree(field);
                }
            }
        } else if (this.assignmentSearchGroup) {
            this.assignmentSearchGroup.addToGroups(PlaybookSearchResults.assignmentSearchGroupDef[PlaybookSearchResults.sortByField].valueConvertor(assignmentToInsert), assignmentToInsert);
        }
    }

    public addAssignment(assignment: StorytellingAssignmentRecord) {
        this.assignments.push(assignment);
        this.byGuid[assignment.Guid] = assignment;
    }

    public remove(rec: StorytellingRecord) {
        super.remove(rec);

        if (rec.Type === ObjectTypes.TOPIC) {
            for (let i = 0; i < this.topics.length; i++) {
                if (this.topics[i].Guid === rec.Guid) {
                    this.topics.splice(i, 1);
                    break;
                }
            }
            for (const field in PlaybookSearchResults.topicGroupDefs) {
                if (this.topicGroups[field]) this.topicGroups[field].remove(PlaybookSearchResults.topicGroupDefs[field].valueConvertor(rec), <StorytellingTopicRecord>rec);
            }

            if (this.topicSearchGroup) {
                this.topicSearchGroup.remove(PlaybookSearchResults.topicGroupDefs.SingleGroup.valueConvertor(rec), <StorytellingTopicRecord>rec);
            }
        } else if (rec.Type === ObjectTypes.SCRIPT) {
            for (let i = 0; i < this.stories.length; i++) {
                if (this.stories[i].Guid === rec.Guid) {
                    this.stories.splice(i, 1);
                    break;
                }
            }
            for (const field in PlaybookSearchResults.storyGroupDefs) {
                if (this.storyGroups[field]) this.storyGroups[field].remove(PlaybookSearchResults.storyGroupDefs[field].valueConvertor(rec), <StorytellingStoryRecord>rec);
            }

            if (this.storySearchGroup) {
                this.storySearchGroup.remove(PlaybookSearchResults.storyGroupDefs.SingleGroup.valueConvertor(rec), <StorytellingStoryRecord>rec);
            }
        } else if (rec.Type === ObjectTypes.ASSIGNMENT) {
            for (let i = 0; i < this.assignments.length; i++) {
                if (this.assignments[i].Guid === rec.Guid) {
                    this.assignments.splice(i, 1);
                    break;
                }
            }
            for (const field in PlaybookSearchResults.assignmentGroupDefs) {
                if (this.assignmentGroups[field]) {
                    this.assignmentGroups[field].remove(PlaybookSearchResults.assignmentGroupDefs[field].valueConvertor(rec), <StorytellingAssignmentRecord>rec);
                }
            }
            if (this.assignmentSearchGroup) {
                this.assignmentSearchGroup.remove(PlaybookSearchResults.assignmentSearchGroupDef.Title.valueConvertor(rec), <StorytellingAssignmentRecord>rec);
            }
        }
        this.buildTree(PlaybookSearchResults.groupByField);
    }

    public dispose() {
        super.dispose();

        this.objectType = null;
        this.topics = [];
        this.stories = [];
        this.assignments = [];
        this.users = [];
        this.topicGroups = {};
        this.storyGroups = {};
        this.assignmentSearchGroup = null;
        this.topicSearchGroup = null;
        this.storySearchGroup = null;
        this.assignmentGroups = {};
    }

    public getGroups(): Array<string> {
        switch (this.objectType) {
            case ObjectTypes.TOPIC:
                return this.topicGroupFields;
            case ObjectTypes.SCRIPT:
                return this.storyGroupFields;
            case ObjectTypes.ASSIGNMENT:
                return this.assignmentGroupFields;
            default:
                return ['Type'];
        }
    }

    public getGroupCount(key: string): number {
        switch (this.objectType) {
            case ObjectTypes.TOPIC:
                return this.topicGroups[key]?.keys?.length || 0;
            case ObjectTypes.SCRIPT:
                return this.storyGroups[key]?.keys?.length || 0;
            case ObjectTypes.ASSIGNMENT:
                return this.assignmentGroups[key]?.keys?.length || 0;
            default:
                return 0;
        }
    }

    public async rebuildGroups(groupBy: string, sortBy: any, sortOrder: string, groupSortOrder: string) {
        if (this.objectType === ObjectTypes.TOPIC) {
            // Clear all groups
            if (!this.topicGroups[groupBy]) this.topicGroups[groupBy] = new ResultGroup<StorytellingTopicRecord>();
            this.topicGroups[groupBy].clear();

            if (!this.topicGroups[PlaybookSearchResults.groupByField]) {
                PlaybookSearchResults.groupByField = groupBy;
            }
            // Build active group
            for (const topic of this.topics) {
                if (this.topicGroups[groupBy]) {
                    this.topicGroups[groupBy]?.addToGroups(PlaybookSearchResults?.topicGroupDefs?.[groupBy]?.valueConvertor(topic), topic);
                }
            }
            this.topicGroups[groupBy].sort(
                PlaybookSearchResults.topicGroupDefs?.[groupBy]?.groupKeyComparator(groupSortOrder),
                PlaybookSearchResults.topicGroupDefs?.[sortBy]?.recordComparator(sortOrder)
            );
            this.topicGroups[groupBy].isBuilt = true;
            this.buildTree(groupBy);
        } else if (this.objectType === ObjectTypes.SCRIPT) {
            // Clear all groups
            if (!this.storyGroups[groupBy]) this.storyGroups[groupBy] = new ResultGroup<StorytellingStoryRecord>();
            this.storyGroups[groupBy].clear();

            if (!this.storyGroups[PlaybookSearchResults.groupByField]) {
                PlaybookSearchResults.groupByField = groupBy;
            }
            // Build active group
            for (const story of this.stories) {
                if (this.storyGroups[groupBy]) {
                    this.storyGroups[groupBy]?.addToGroups(PlaybookSearchResults?.storyGroupDefs?.[groupBy]?.valueConvertor(story), story);
                }
            }
            this.storyGroups[groupBy].sort(
                PlaybookSearchResults.storyGroupDefs?.[groupBy]?.groupKeyComparator(groupSortOrder),
                PlaybookSearchResults.storyGroupDefs?.[sortBy]?.recordComparator(sortOrder)
            );
            this.storyGroups[groupBy].isBuilt = true;
            this.buildTree(groupBy);
        } else if (this.objectType === ObjectTypes.ASSIGNMENT) {
            // Clear all groups
            if (!this.assignmentGroups[groupBy]) this.assignmentGroups[groupBy] = new ResultGroup<StorytellingAssignmentRecord>();
            this.assignmentGroups[groupBy].clear();

            if (!this.assignmentGroups[PlaybookSearchResults.groupByField]) {
                PlaybookSearchResults.groupByField = groupBy;
            }
            // Build active group
            for (const assignment of this.assignments) {
                if (this.assignmentGroups[groupBy]) {
                    this.assignmentGroups[groupBy].addToGroups(PlaybookSearchResults.assignmentGroupDefs?.[groupBy]?.valueConvertor(assignment), assignment);
                }
            }
            this.assignmentGroups[groupBy].sort(
                PlaybookSearchResults.assignmentGroupDefs?.[groupBy]?.groupKeyComparator(groupSortOrder),
                PlaybookSearchResults.assignmentGroupDefs?.[sortBy]?.recordComparator(sortOrder)
            );
            this.assignmentGroups[groupBy].isBuilt = true;
            this.buildTree(groupBy);
        } else {
            this.assignmentSearchGroup = new ResultGroup<StorytellingAssignmentRecord>();
            for (const assignment of this.assignments) {
                if (this.assignmentSearchGroup) {
                    this.assignmentSearchGroup.addToGroups(PlaybookSearchResults.assignmentGroupDefs?.Title?.valueConvertor(assignment), assignment);
                }
            }

            this.topicSearchGroup = new ResultGroup<StorytellingTopicRecord>();
            for (const topic of this.topics) {
                if (this.topicSearchGroup) {
                    this.topicSearchGroup.addToGroups(PlaybookSearchResults.topicGroupDefs?.SingleGroup?.valueConvertor(topic), topic);
                }
            }

            this.storySearchGroup = new ResultGroup<StorytellingStoryRecord>();
            for (const story of this.stories) {
                if (this.topicSearchGroup) {
                    this.topicSearchGroup.addToGroups(PlaybookSearchResults.storyGroupDefs?.SingleGroup?.valueConvertor(story), story);
                }
            }

            this.topicSearchGroup.sort(
                PlaybookSearchResults.topicGroupDefs?.SingleGroup?.groupKeyComparator(groupSortOrder),
                PlaybookSearchResults.topicGroupDefs?.[sortBy]?.recordComparator(sortOrder)
            );
            this.assignmentSearchGroup.sort(
                PlaybookSearchResults.assignmentGroupDefs?.Title?.groupKeyComparator(groupSortOrder),
                PlaybookSearchResults.assignmentGroupDefs?.[sortBy]?.recordComparator(sortOrder)
            );
            this.storySearchGroup.sort(
                PlaybookSearchResults.storyGroupDefs?.SingleGroup?.groupKeyComparator(groupSortOrder),
                PlaybookSearchResults.storyGroupDefs?.[sortBy]?.recordComparator(sortOrder)
            );
            this.topicSearchGroup.isBuilt = true;
            this.assignmentSearchGroup.isBuilt = true;
            this.storySearchGroup.isBuilt = true;

            this.buildTree(groupBy);
        }
    }

    override buildTree(groupBy: string): void {
        this.treeDataSource.disconnect();
        this.treeDataSource
            .connect(this)
            .pipe(
                map(
                    data => {
                        this.cardItemHeights = data.map(item => {
                            switch (this.objectType) {
                                case ObjectTypes.ASSIGNMENT: {
                                    switch (item.level) {
                                        case 0:
                                            return 32;
                                        case 1: {
                                            // if (item.isNewItems) {
                                            //     if (item.nodeLastInGroup) {
                                            //         return 144;
                                            //     }
                                            //     return 136;
                                            // }
                                            return 32;
                                        }
                                        default: {
                                            if (item.nodeLastInGroup) {
                                                return 152;
                                            }
                                            return 144;
                                        }
                                    }
                                }
                                case ObjectTypes._SEARCH: {
                                    switch (item.level) {
                                        case 0:
                                            return 32;
                                        case 1: {
                                            if (item.object.Type === ObjectTypes.TOPIC) {
                                                if (item.nodeLastInGroup) {
                                                    return 152;
                                                }
                                                return 144;
                                            }
                                            return 32;
                                        }
                                        default:
                                            return 144;
                                    }
                                }
                                default: {
                                    switch (item.level) {
                                        case 0:
                                            return 32;
                                        default: {
                                            if (item.nodeLastInGroup) {
                                                return 152;
                                            }
                                            return 144;
                                        }
                                    }
                                }
                            }
                        });
                        this.condensedItemHeights = data.map(item => {
                            switch (this.objectType) {
                                // TODO: Probably will end up implementing this at some point, but for now we dont know.
                                // case ObjectTypesEnum.ASSIGNMENT: {
                                //     switch (item.level) {
                                //         case 0: return 40;
                                //         case 1: {
                                //             if (item.isNewItems) {
                                //                 if (item.nodeLastInGroup) {
                                //                     return 144;
                                //                 }
                                //                 return 136;
                                //             }
                                //             return 32;
                                //         }
                                //         default: return 80;
                                //     }
                                // }
                                default: {
                                    switch (item.level) {
                                        case 0:
                                            return 40;
                                        default: {
                                            if (item.nodeLastInGroup && item.nodeFirstInGroup) {
                                                return 80;
                                            }
                                            if (item.nodeLastInGroup) {
                                                return 56;
                                            }
                                            if (item.nodeFirstInGroup) {
                                                return 72;
                                            }
                                            return 44;
                                        }
                                    }
                                }
                            }
                        });

                        this.cardItemHeightsCustomFields = data.map(item => {
                            switch (this.objectType) {
                                case ObjectTypes.ASSIGNMENT: {
                                    switch (item.level) {
                                        case 0:
                                            return 32;
                                        case 1: {
                                            // if (item.isNewItems) {
                                            //     if (item.nodeLastInGroup) {
                                            //         return 144;
                                            //     }
                                            //     return 136;
                                            // }
                                            return 32;
                                        }
                                        default: {
                                            if (item.nodeLastInGroup) {
                                                return 152;
                                            }
                                            return 144;
                                        }
                                    }
                                }
                                case ObjectTypes._SEARCH: {
                                    switch (item.level) {
                                        case 0:
                                            return 32;
                                        case 1: {
                                            if (item.object.Type === ObjectTypes.TOPIC) {
                                                if (item.nodeLastInGroup) {
                                                    return 188;
                                                }
                                                return 180;
                                            }
                                            return 32;
                                        }
                                        default: {
                                            if (item.nodeLastInGroup) {
                                                return 152;
                                            }
                                            return 144;
                                        }
                                    }
                                }
                                default: {
                                    switch (item.level) {
                                        case 0:
                                            return 32;
                                        default: {
                                            if (item.nodeLastInGroup) {
                                                return 188;
                                            }
                                            return 180;
                                        }
                                    }
                                }
                            }
                        });
                    },

                    takeUntil(this.onDispose)
                )
            )
            .subscribe();

        switch (this.objectType) {
            case ObjectTypes.TOPIC: {
                this.treeLikeData = this.topicGroups[groupBy].keys.map<TreeLikeNode<StorytellingRecord>>(key => ({
                    id: key.value,
                    label: key.display,
                    itemsCount: this.topicGroups[groupBy].entries[key.value].length,
                    children: this.topicGroups[groupBy].entries[key.value].map<TreeLikeNode<StorytellingRecord>>((entry, index) => ({
                        id: entry.Guid,
                        children: [],
                        object: entry,
                        nodeFirstInGroup: index === 0,
                        nodeLastInGroup: index === this.topicGroups[groupBy].entries[key.value].length - 1,
                    })),
                    // cost: combineLatest([...this.topicGroups[groupBy].entries[key.value].map(entry => entry.totalCostObserver)]).pipe(map(costs => costs.reduce((acc, cost) => acc + cost, 0))),
                }));
                break;
            }
            case ObjectTypes.SCRIPT: {
                this.treeLikeData = this.storyGroups[groupBy].keys.map<TreeLikeNode<StorytellingRecord>>(key => ({
                    id: key.value,
                    label: key.display,
                    itemsCount: this.storyGroups[groupBy].entries[key.value].length,
                    children: this.storyGroups[groupBy].entries[key.value].map<TreeLikeNode<StorytellingRecord>>((entry, index) => ({
                        id: entry.Guid,
                        children: [],
                        object: entry,
                        nodeFirstInGroup: index === 0,
                        nodeLastInGroup: index === this.storyGroups[groupBy].entries[key.value].length - 1,
                    })),
                }));
                break;
            }
            case ObjectTypes.ASSIGNMENT: {
                this.treeLikeData = this.assignmentGroups[groupBy].keys.map<TreeLikeNode<StorytellingRecord>>(key => ({
                    id: key.value,
                    label: key.display,
                    itemsCount: this.assignmentGroups[groupBy].entries[key.value].length,
                    children: this.assignmentGroups[groupBy].entries[key.value].reduce((acc, entry, index) => {
                        const parent = acc.find(item => item.id === entry.ParentGuid);
                        if (!parent) {
                            acc.push({
                                id: entry.ParentGuid,
                                label: entry.ContainerTitle,
                                object: entry,
                                isMetadata: true,
                                children: [
                                    {
                                        id: entry.Guid,
                                        children: [],
                                        object: entry,
                                        nodeFirstInGroup: index === 0,
                                        nodeLastInGroup: index === this.assignmentGroups[groupBy].entries[key.value].length - 1,
                                    },
                                ],
                            });
                        } else {
                            parent.children.push({
                                id: entry.Guid,
                                children: [],
                                object: entry,
                                nodeFirstInGroup: index === 0,
                                nodeLastInGroup: index === this.assignmentGroups[groupBy].entries[key.value].length - 1,
                            });
                        }
                        return acc;
                    }, [] as TreeLikeNode<StorytellingRecord>[]),
                    // cost: combineLatest([...this.assignmentGroups[groupBy].entries[key.value].map(entry => entry.totalCostObserver)]).pipe(map(costs => costs.reduce((acc, cost) => acc + cost, 0))),
                }));
                break;
            }
            case ObjectTypes._SEARCH: {
                this.treeLikeData = [];

                if (this.topicSearchGroup && this.topicSearchGroup.entries.SingleGroup && this.topicSearchGroup.entries.SingleGroup.length > 0) {
                    const topics = {
                        id: 'topics',
                        label: 'Topics',
                        itemsCount: this.topicSearchGroup.entries.SingleGroup.length,
                        children: this.topicSearchGroup.entries.SingleGroup.map<TreeLikeNode<StorytellingRecord>>((entry, idx) => ({
                            id: entry.Guid,
                            children: [],
                            object: entry,
                            nodeFirstInGroup: idx === 0,
                            nodeLastInGroup: idx === this.topicSearchGroup.entries.SingleGroup.length - 1,
                        })),
                    };
                    this.treeLikeData = [...this.treeLikeData, topics];
                }

                if (this.assignmentSearchGroup && this.assignmentSearchGroup.keys && this.assignmentSearchGroup.keys.length > 0) {
                    const assignments = this.assignmentSearchGroup.keys.reduce((grpAcc, key) => {
                        const childrenReducer = (childrenAcc, entry, index) => {
                            const parent = childrenAcc.find(item => item.id === entry.parentGuid);
                            if (!parent) {
                                childrenAcc.push({
                                    id: entry.parentGuid,
                                    label: entry.parentRecord.title,
                                    object: entry,
                                    isMetadata: true,
                                    children: [
                                        {
                                            id: entry.guid,
                                            children: [],
                                            object: entry,
                                            nodeFirstInGroup: index === 0,
                                            nodeLastInGroup: false,
                                        },
                                    ],
                                });
                            } else {
                                parent.children.push({
                                    id: entry.guid,
                                    children: [],
                                    object: entry,
                                    nodeFirstInGroup: index === 0,
                                    nodeLastInGroup: false,
                                });
                            }
                            return childrenAcc;
                        };

                        if (grpAcc.id) {
                            grpAcc.children = [...grpAcc.children, ...this.assignmentSearchGroup.entries[key.value].reduce(childrenReducer, [] as TreeLikeNode<StorytellingRecord>[])];
                        } else {
                            grpAcc = {
                                id: 'assignments',
                                label: 'Assignments',
                                itemsCount: this.assignments.length,
                                children: this.assignmentSearchGroup.entries[key.value].reduce(childrenReducer, [] as TreeLikeNode<StorytellingRecord>[]),
                            };
                        }
                        return grpAcc;
                    }, {} as TreeLikeNode<StorytellingRecord>);
                    this.treeLikeData = [...this.treeLikeData, assignments];
                }
                break;
            }
        }
        this.treeDataSource.data = this.treeLikeData;
        this.treeControl.expandAll();
    }
}

DataStore.registerSearchResults('playbook', new PlaybookSearchResults());
